import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * PasswordCriteriaValidator Component
 * 
 * Displays password requirements with live validation feedback
 * Shows initial state before typing, then updates rules with ✓/✗ as user types
 */
const PasswordCriteriaValidator = ({ password = '', isVisible = false }) => {
    const { t } = useLanguage();

    // Define password criteria validation
    const criteria = useMemo(() => {
        const hasValue = password.length > 0;

        return [
            {
                id: 'length',
                label: t('passwordCriteria.length'),
                test: (pwd) => pwd.length >= 12,
            },
            {
                id: 'special',
                label: t('passwordCriteria.special'),
                test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
            },
            {
                id: 'uppercase',
                label: t('passwordCriteria.uppercase'),
                test: (pwd) => /[A-Z]/.test(pwd),
            },
            {
                id: 'lowercase',
                label: t('passwordCriteria.lowercase'),
                test: (pwd) => /[a-z]/.test(pwd),
            },
            {
                id: 'number',
                label: t('passwordCriteria.number'),
                test: (pwd) => /[0-9]/.test(pwd),
            },
        ].map((rule) => ({
            ...rule,
            isSatisfied: hasValue ? rule.test(password) : null, // null = not started typing yet
        }));
    }, [password, t]);

    const isInitialState = password.length === 0;
    const headerText = t('passwordCriteria.header');

    if (!isVisible) {
        return null;
    }

    return (
        <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <p className="text-xs text-gray-700 mb-2 font-medium">
                {headerText}
            </p>
            <ul className="space-y-1.5">
                {criteria.map((rule) => {
                    return (
                        <li key={rule.id} className="flex items-start gap-2 text-xs">
                            {isInitialState ? (
                                // Initial state: show neutral bullet
                                <span className="w-1 h-1 rounded-full bg-gray-400 flex-shrink-0 mt-1.5"></span>
                            ) : rule.isSatisfied ? (
                                // Satisfied: show green check
                                <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                            ) : (
                                // Not satisfied: show red X
                                <X className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                            )}
                            <span
                                className={
                                    isInitialState
                                        ? 'text-gray-600'
                                        : rule.isSatisfied
                                            ? 'text-green-700'
                                            : 'text-red-600'
                                }
                            >
                                {rule.label}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default PasswordCriteriaValidator;
